/**
 * Onboarding — lista e proceseve, jo lista e detyrave.
 *
 * Më parë kjo faqe lexonte çdo detyrë të çdo punonjësi dhe i grumbullonte te
 * shfletuesi. Ajo pamje s'mund t'u përgjigjej pyetjeve që HR-ja bën vërtet: kush
 * fillon këtë javë, cili proces ka mbetur pas, a është dikush gati për ditën e
 * parë. Tani një rresht është një person që po vjen në punë, me përparimin,
 * pronarin dhe vonesat e tij.
 *
 * Detyrat e vjetra — ato që ekzistonin para se onboarding-u të bëhej proces —
 * nuk fshihen dhe nuk u shpiken procese. Ato rrinë poshtë, si vetja e tyre.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, LinearProgress,
  MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useHistory } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import usePermissions from '../hooks/usePermissions';
import onboardingService from '../services/onboardingService';
import { instanceMeta, INSTANCE_STATUSES, dayOneText } from '../config/onboarding';

const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

function Kpi({ label, value, hint, warn }) {
  return (
    <Card sx={{ flex: 1, ...(warn && value > 0 ? { borderLeft: '4px solid #D97706' } : {}) }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: warn && value > 0 ? '#B45309' : undefined }}>{value}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function OnboardingPage() {
  const history = useHistory();
  const { can } = usePermissions();
  const canManage = can('onboarding.manage');

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({ search: '', status: 'active', owner: 'all', overdue: 'all', from: '', to: '' });
  const [legacy, setLegacy] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    onboardingService().legacyTasks().then((d) => setLegacy(Array.isArray(d) ? d : []));
    onboardingService().templates().then((d) => setTemplates(Array.isArray(d) ? d : []));
  }, []);

  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);
  useEffect(() => { setPage(0); }, [debounced, filters.status, filters.owner, filters.overdue, filters.from, filters.to]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const d = await onboardingService().list({
      page: page + 1, limit: rowsPerPage, search: debounced,
      status: filters.status, owner: filters.owner,
      overdue: filters.overdue === 'yes' ? 'true' : '',
      from: filters.from, to: filters.to,
    });
    if (d.error) setError(d.error);
    setRows(d.data || []);
    setPagination(d.pagination || pagination);
    setStats(d.stats || null);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debounced, filters.status, filters.owner, filters.overdue, filters.from, filters.to]);

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const seedTemplate = async () => {
    const r = await onboardingService().createStandardTemplate();
    if (r?.error) { setError(r.error); return; }
    setFlash(`Shablloni «${r.name}» u krijua me ${r.taskCount} detyra. Rregulloje sipas kompanisë para se ta përdorësh.`);
    onboardingService().templates().then((d) => setTemplates(Array.isArray(d) ? d : []));
  };

  return (
    <Box>
      <PageHeader title="Onboarding"
        subtitle="Nga oferta e pranuar te dita e parë — kush e ka secilën detyrë dhe çfarë ka mbetur" />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <Kpi label="Procese aktive" value={stats?.active ?? 0} hint="planifikuar, në zhvillim ose të bllokuara" />
        <Kpi label="Fillojnë këtë javë" value={stats?.starting_week ?? 0} hint="brenda shtatë ditëve" />
        <Kpi label="Me detyra të vonuara" value={stats?.overdue ?? 0} hint="afati ka kaluar" warn />
        <Kpi label="Përfunduar këtë muaj" value={stats?.completed_month ?? 0} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {flash && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFlash('')}>{flash}</Alert>}

      {/* Pa shabllon, çdo proces do të shkruhej me dorë — pikërisht puna që ky
          modul ekziston ta heqë. Prandaj mungesa e tij thuhet hapur. */}
      {templates.length === 0 && canManage && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button size="small" onClick={seedTemplate}>Krijo shabllonin standard</Button>}>
          Ende s'ka asnjë shabllon. Pa të, çdo proces do të duhej shkruar nga e para për secilin punonjës.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Kërko" placeholder="Emër ose pozitë" value={filters.search}
              onChange={setF('search')} sx={{ flex: 1, minWidth: 190 }} />
            <TextField select size="small" label="Gjendja" value={filters.status} onChange={setF('status')} sx={{ minWidth: 160 }}>
              <MenuItem value="active">Aktive</MenuItem>
              <MenuItem value="all">Të gjitha</MenuItem>
              {INSTANCE_STATUSES.map((s) => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Pronari" value={filters.owner} onChange={setF('owner')} sx={{ minWidth: 150 }}>
              <MenuItem value="all">Të gjithë</MenuItem>
              <MenuItem value="me">Unë</MenuItem>
            </TextField>
            <TextField select size="small" label="Vonesat" value={filters.overdue} onChange={setF('overdue')} sx={{ minWidth: 150 }}>
              <MenuItem value="all">Të gjitha</MenuItem>
              <MenuItem value="yes">Vetëm me vonesa</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Fillimi prej" InputLabelProps={{ shrink: true }} value={filters.from} onChange={setF('from')} />
            <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }} value={filters.to} onChange={setF('to')} />
            <Button size="small" variant="outlined"
              onClick={() => setFilters({ search: '', status: 'active', owner: 'all', overdue: 'all', from: '', to: '' })}>Reseto</Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <HowToRegRoundedIcon sx={{ color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Asnjë proces nuk i përgjigjet filtrave. Një proces nis nga Punësimet, me veprimin «Fillo onboarding».
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Punonjësi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pozita</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Departamenti</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fillimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pronari</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>Përparimi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dita e parë</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Gjendja</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const st = instanceMeta(r.status);
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{r.employee}</Typography>
                          {!r.workerId && (
                            <Tooltip title="Paga dhe kontrata varen nga kjo lidhje">
                              <Typography sx={{ fontSize: 11, color: '#B45309' }}>ende pa lidhje me Stafin</Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.position || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.departmentName || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(r.startDate)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.ownerName || '—'}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                            {r.progress.completed}/{r.progress.total}
                          </Typography>
                          <LinearProgress variant="determinate" value={r.progress.percent}
                            sx={{ height: 5, borderRadius: 3, mt: 0.25 }} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" sx={{ height: 20, fontSize: '.64rem' }}
                            color={!r.dayOne.defined ? 'default' : r.dayOne.ready ? 'success' : 'warning'}
                            variant={r.dayOne.ready ? 'filled' : 'outlined'}
                            label={dayOneText(r.dayOne)} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" color={st.color} variant="outlined" label={st.label} sx={{ height: 20, fontSize: '.66rem' }} />
                            {r.overdue > 0 && (
                              <Tooltip title={`${r.overdue} detyra me afat të kaluar`}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#D97706' }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" sx={{ textTransform: 'none', fontSize: '.75rem' }}
                            onClick={() => history.push(`/onboarding/${r.id}`)}>Hap</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={Math.min(page, Math.max(0, pagination.totalPages - 1))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Rreshta:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            />
          )}
        </CardContent>
      </Card>

      {/* Detyrat e trashëguara: pa proces, pa shpikje, por të dukshme. */}
      {legacy.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 800, fontSize: '.9rem' }}>Pa proces onboarding</Typography>
            <Typography variant="caption" color="text.secondary">
              Detyra të krijuara para se onboarding-u të bëhej proces. Nuk u lidhën me asnjë proces,
              sepse një lidhje e hamendësuar do të dukej si e dhënë e vërtetë.
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              {legacy.map((t) => (
                <Stack key={t.id} direction="row" spacing={1} alignItems="center"
                  sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography sx={{ fontSize: 13, flex: 1 }}>{t.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.workerName || 'pa punonjës'}</Typography>
                  <Chip size="small" label={t.status === 'completed' ? 'E kryer' : 'Në pritje'} sx={{ height: 19, fontSize: '.64rem' }} />
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
